import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Button, ComposerWindow, Text, TextInput } from '@/components/ui';
import { useCreateCollectionsAction } from '@/hooks/use-accounting';
import { useComposerDraft } from '@/hooks/use-composer-draft';
import { useAssociation } from '@/hooks/use-data';
import type { CollectionsActionType } from '@/types/models';
import { Alert } from '@/utils/alert';
import { formatBusinessDate, todayBusinessMs } from '@/utils/date-utils';
import { formatMoneyCents } from '@/utils/format-money';
import { mergeTemplate } from '@/utils/template-merge';

const FALLBACK_TEMPLATES: Record<'letter_first_notice' | 'letter_second_notice' | 'letter_intent_to_lien', string> = {
	letter_first_notice:
		'{{today}}\n\n{{accountName}}\n{{address}}\n\nRe: Past-due assessments — {{associationName}}\n\nOur records show your account at {{address}} has an outstanding balance of {{balance}}. Please remit payment promptly to avoid further collection action.\n\nThank you,\n{{associationName}}',
	letter_second_notice:
		'{{today}}\n\n{{accountName}}\n{{address}}\n\nRe: Second notice — past-due assessments\n\nDespite our previous notice, your balance of {{balance}} remains unpaid ({{daysOverdue}} days overdue). Continued non-payment may result in additional fees and lien action.\n\n{{associationName}}',
	letter_intent_to_lien:
		'{{today}}\n\n{{accountName}}\n{{address}}\n\nNOTICE OF INTENT TO LIEN\n\nYour balance of {{balance}} is {{daysOverdue}} days past due. Unless full payment is received within 30 days, a lien will be filed against your property at {{address}}.\n\n{{associationName}}',
};

const ACTION_LABELS: Record<keyof typeof FALLBACK_TEMPLATES, string> = {
	letter_first_notice: '1st notice',
	letter_second_notice: '2nd notice',
	letter_intent_to_lien: 'Intent to lien',
};

const TEMPLATE_FIELD: Record<keyof typeof FALLBACK_TEMPLATES, 'collectionsFirstNoticeTemplate' | 'collectionsSecondNoticeTemplate' | 'collectionsIntentToLienTemplate'> = {
	letter_first_notice: 'collectionsFirstNoticeTemplate',
	letter_second_notice: 'collectionsSecondNoticeTemplate',
	letter_intent_to_lien: 'collectionsIntentToLienTemplate',
};

export interface CollectionsLetterModalProps {
	visible: boolean;
	onDismiss: () => void;
	accountId: string;
	associationId: string;
	accountName: string;
	address?: string;
	balanceCents: number;
	daysOverdue?: number;
	onLogged?: () => void;
	/** Open collapsed — used when `ComposerHost` restores a draft after a page reload. */
	startMinimized?: boolean;
	/** Bumped by the host to bring an already-open, collapsed composer back to the front. */
	expandRequest?: number;
	/** Lets the host warn before a click replaces or closes work in progress. */
	onDirtyChange?: (dirty: boolean) => void;
	/** The signed-in user id, from `ComposerHost`. Absent means do not persist a draft. */
	draftScope?: string | null;
}

/**
 * The notice TYPE is persisted alongside the text.
 *
 * Without it a restored draft would come back as a first notice carrying the wording of an
 * intent-to-lien — the body would be right and the audit row written by "Log as sent" would
 * be wrong, which is the worse half to get wrong on a collections letter.
 */
interface CollectionsLetterDraft {
	letterType: keyof typeof FALLBACK_TEMPLATES;
	body: string;
}

export const CollectionsLetterModal: React.FC<CollectionsLetterModalProps> = ({
	visible, onDismiss, accountId, associationId, accountName, address, balanceCents, daysOverdue, onLogged,
	startMinimized, expandRequest, onDirtyChange, draftScope,
}) => {
	const { data: association } = useAssociation(associationId);
	const logAction = useCreateCollectionsAction();
	const [letterType, setLetterType] = useState<keyof typeof FALLBACK_TEMPLATES>('letter_first_notice');
	const [body, setBody] = useState('');
	const [dirty, setDirty] = useState(false);

	const template = useMemo(() => {
		const fromAssoc = association?.[TEMPLATE_FIELD[letterType]] as string | undefined;
		return fromAssoc?.trim() || FALLBACK_TEMPLATES[letterType];
	}, [association, letterType]);

	/**
	 * Merge the template, but never over the top of an edited letter.
	 *
	 * The `dirty` guard matters because this effect depends on the association query and on
	 * six row fields — a refetch on window focus re-ran it and wiped whatever had been typed.
	 * Switching NOTICE TYPE deliberately clears `dirty` (see the picker below) so that stays
	 * a reseed rather than becoming a no-op.
	 */
	useEffect(() => {
		if (dirty) return;
		const merged = mergeTemplate(template, {
			// The letter's printed date must match the ET business date the
			// logged action records (occurredOn below) — viewer-local rendering
			// let a late-evening letter disagree with its own audit trail.
			today: formatBusinessDate(todayBusinessMs()),
			accountName,
			address: address ?? '',
			balance: formatMoneyCents(balanceCents),
			balanceCents: String(balanceCents),
			daysOverdue: daysOverdue ?? 0,
			associationName: association?.name ?? '',
		});
		setBody(merged);
	}, [dirty, template, accountName, address, balanceCents, daysOverdue, association?.name]);

	/**
	 * Restore whatever was typed before the page was reloaded.
	 *
	 * Order against the merge above does not matter: if this runs first it sets `dirty` and
	 * the merge skips; if the merge runs first this overwrites it. The reviewer's own words
	 * win either way.
	 */
	const draft = useComposerDraft<CollectionsLetterDraft>(
		draftScope,
		visible ? `collections-letter:${accountId}` : null,
	);
	const restoredRef = useRef<string | null>(null);
	useEffect(() => {
		if (!visible || !draft.ready || !draft.restored) return;
		if (restoredRef.current === accountId) return;
		restoredRef.current = accountId;
		setLetterType(draft.restored.letterType);
		setBody(draft.restored.body);
		setDirty(true);
	}, [visible, accountId, draft.ready, draft.restored]);

	useEffect(() => {
		if (!visible) { setDirty(false); restoredRef.current = null; }
	}, [visible]);

	// Only once the letter has actually been edited — otherwise merely opening the composer
	// would store the merged template as though it were someone's work.
	useEffect(() => {
		if (!visible || !dirty) return;
		draft.save({ letterType, body });
	}, [visible, dirty, letterType, body, draft]);

	// Same signal the host uses to warn before a click throws this away.
	useEffect(() => { onDirtyChange?.(visible && dirty); }, [visible, dirty, onDirtyChange]);

	const onCopy = () => {
		if (typeof navigator !== 'undefined' && navigator.clipboard) {
			navigator.clipboard.writeText(body).catch(() => {});
		}
	};

	const onLog = () => {
		// Mirrors the button's `disabled`. Cmd+Enter reaches this directly, so two presses
		// would write two `collections_actions` rows for the same account on the same day —
		// e.g. a duplicate "intent to lien" in the delinquency audit trail.
		if (logAction.isPending) return;
		logAction.mutate({
			accountId,
			actionType: letterType as CollectionsActionType,
			occurredOn: todayBusinessMs(),
			notes: 'Letter composed via template',
			balanceAtActionCents: balanceCents,
		}, {
			onSuccess: () => {
				// It is on the record now; a stored draft would resurrect it next visit.
				draft.clear();
				onLogged?.();
				onDismiss();
			},
			onError: (e) => Alert.alert('Could not log letter', e.message),
		});
	};

	const actions = (
		<View style={styles.footer}>
			<Button mode="outlined" onPress={onCopy} icon="content-copy">Copy</Button>
			<Button mode="contained" onPress={onLog} disabled={logAction.isPending} icon="check">
				{logAction.isPending ? 'Logging…' : 'Log as sent'}
			</Button>
		</View>
	);

	return (
		<ComposerWindow
			visible={visible}
			onDismiss={onDismiss}
			onSubmit={onLog}
			busy={logAction.isPending}
			startMinimized={startMinimized}
			expandRequest={expandRequest}
			title="Compose collections letter"
			// Most accounts ARE named by their address, so appending it unconditionally printed
			// the same street twice in the title bar.
			subtitle={address && address !== accountName ? `${accountName} · ${address}` : accountName}
			footer={actions}
			initialWidth={700}
			testID="collections-letter-composer"
		>
			<View style={styles.row}>
				{(Object.keys(ACTION_LABELS) as Array<keyof typeof FALLBACK_TEMPLATES>).map((k) => (
					<Pressable
						key={k}
						onPress={() => {
							// Picking a different notice is an explicit request to RESEED, so it
							// clears `dirty` and lets the merge effect rewrite the body for the
							// new type. Without that the guard above would make the picker inert
							// the moment anyone touched the letter.
							setLetterType(k);
							setDirty(false);
						}}
						style={[styles.option, letterType === k && styles.optionSelected]}
					>
						<Text style={[styles.optionLabel, letterType === k && styles.optionLabelSelected]}>
							{ACTION_LABELS[k]}
						</Text>
					</Pressable>
				))}
			</View>
			<TextInput
				mode="outlined" label="Letter body"
				value={body} onChangeText={(v) => { setBody(v); setDirty(true); }}
				multiline numberOfLines={14}
				style={styles.body}
			/>
		</ComposerWindow>
	);
};

const styles = StyleSheet.create({
	row: { flexDirection: 'row', gap: 8, marginBottom: 12 },
	option: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14, borderWidth: 1, borderColor: '#cfd8dc', backgroundColor: '#fff' },
	optionSelected: { borderColor: '#1976d2', backgroundColor: '#e3f2fd' },
	optionLabel: { fontSize: 13, color: '#455a64' },
	optionLabelSelected: { color: '#1976d2', fontWeight: '600' },
	body: { minHeight: 280 },
	footer: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8 },
});
